import React, { FC, useCallback, useEffect, useState } from "react";
import ChatInterface from "./ChatInterface";
import { aiQueryKeys } from "../../../../../../hooks/ai/types";
import {
  useGetBusinessChatGptInterviewVoiceHistory,
  useGetUserChatGptInterviewVoiceHistory,
  useSaveBusinessInterviewTimeout,
  useSaveUserInterviewTimeout,
} from "../../../../../../hooks/ai/useAI";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useSnapshot } from "valtio";
import { userDataStore } from "../../../../../../store/userData";
import { businessFibiClientStore } from "../../../../../../store/businessFibiClient";
import RequestWrapper from "../../../../components/RequestWrapper";
import { SessionQuery } from "./types";
import useAudio from "./useAudio";
// import UseTTSVisualizer from "./UseTTSVisualizer";
import SessionEnded from "./SessionEnded";
import useAllMessages from "./useAllMessages";
import FiBiVoiceBottomBar from "./FiBiVoiceBottomBar";
import { utilsQueryKeys } from "../../../../../../hooks/utils/types";
import { FibiAiQuery } from "./data";
import AiDisclaimer from "../../../../components/AiDisclaimer";
import EnhancedBanner from "../../components/EnhancedBanner";

interface Props {
  changeSessionCompleted: (second: boolean) => void;
  onInterviewOff: () => void;
  onShowingAnalysis: (second: boolean) => void;
}

const BusinessMain: FC<Props> = ({
  changeSessionCompleted,
  onInterviewOff,
  onShowingAnalysis,
}) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionTemp = searchParams.get(SessionQuery.sessionCount);
  const fibiUserType = searchParams.get(FibiAiQuery.userType);
  const { userData } = useSnapshot(userDataStore);
  const { fibiClientToken, storedCurrentSessionStartTime } = useSnapshot(
    businessFibiClientStore
  );
  const [sidebarItems, setSidebarItems] = useState<
    { label: string; count: number }[]
  >([]);
  const [interimTranscript, setInterimTranscript] = useState("");
  // const [isMobile, setIsMobile] = useState<boolean>(false);
  // const [isSmaller, setIsSmaller] = useState<boolean>(false);

  const onInterim = useCallback((second: string) => {
    setInterimTranscript(second);
  }, []);

  // useEffect(() => {
  //   if (window.innerWidth < 768) setIsMobile(true);
  // }, []);

  // useEffect(() => {
  //   if (window.innerWidth < 500) setIsSmaller(true);
  // }, []);

  const {
    data: bData,
    isFetching: bLoading,
    isError: bIsError,
  } = useGetBusinessChatGptInterviewVoiceHistory(
    sessionTemp || "",
    fibiClientToken || "",
    !fibiUserType
  );

  const {
    data: uData,
    isFetching: uLoading,
    isError: uIsError,
  } = useGetUserChatGptInterviewVoiceHistory(sessionTemp || "", !!fibiUserType);

  const historyData = bData || uData;
  const historyLoading = bLoading || uLoading;
  const historyIsError = bIsError || uIsError;
  const { mutateAsync } = useSaveBusinessInterviewTimeout();
  const { mutateAsync: userMutateAsync } = useSaveUserInterviewTimeout();
  const { audioRef, audioContextRef } = useAudio();
  // const { audioRef, analyserRef, audioContextRef, mediaSourceRef } = useAudio();
  const { allMessages, onAddToChatHistory, onReset } = useAllMessages({
    historyLoading,
    historyData: historyData?.result,
  });

  const sessionCompleted = !!historyData?.sessionCompleted;

  useEffect(() => {
    changeSessionCompleted(sessionCompleted);
  }, [sessionTemp, changeSessionCompleted, sessionCompleted]);

  useEffect(() => {
    onReset();
  }, [onReset, sessionTemp]);

  useEffect(() => {
    if (!userData) return;

    const count = userData.interviewCount ?? 1;

    if (sessionTemp) {
      if (+sessionTemp <= count) return;
      const temp = new Array(+sessionTemp).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    } else {
      searchParams.set(SessionQuery.sessionCount, `${count}`);
      setSearchParams(searchParams);

      const temp = new Array(count).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    }
  }, [searchParams, sessionTemp, setSearchParams, userData]);

  useEffect(() => {
    if (!userData) return;

    const count = userData.interviewCount ?? 1;

    if (count > sidebarItems.length) {
      const temp = new Array(count).fill(1).map((ev, index) => ({
        label: `Session ${index + 1}`,
        count: index + 1,
      }));

      setSidebarItems(temp);
    }
  }, [sidebarItems.length, userData]);

  const onEnd = useCallback(async () => {
    if (fibiUserType) {
      if (!sessionTemp) return;
      return await userMutateAsync(
        {
          sessionCount: sessionTemp,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              aiQueryKeys.getUserChatGptInterviewVoiceHistory,
            ]);
            queryClient.invalidateQueries([utilsQueryKeys.getUserData]);
            onReset();
            businessFibiClientStore.storedCurrentSessionStartTime = undefined;
            onShowingAnalysis(true);
          },
        }
      );
    } else {
      if (!sessionTemp || !fibiClientToken) return;

      return await mutateAsync(
        {
          sessionCount: sessionTemp,
          businessId: fibiClientToken,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([
              aiQueryKeys.getBusinessChatGptInterviewVoiceHistory,
            ]);
            queryClient.invalidateQueries([utilsQueryKeys.getUserData]);
            onReset();
            businessFibiClientStore.storedCurrentSessionStartTime = undefined;
          },
        }
      );
    }
  }, [
    fibiUserType,
    sessionTemp,
    userMutateAsync,
    queryClient,
    onReset,
    onShowingAnalysis,
    fibiClientToken,
    mutateAsync,
  ]);

  useEffect(() => {
    if (!storedCurrentSessionStartTime) return;
    if (storedCurrentSessionStartTime !== null) {
      const TEN_MINUTES = 10 * 60 * 1000;
      if (Date.now() >= storedCurrentSessionStartTime + TEN_MINUTES) {
        onEnd();
      }
    }
  }, [onEnd, storedCurrentSessionStartTime]);

  const hasPlan = userData?.fibiSubInfo?.subscriptionType;

  return (
    <RequestWrapper
      isLoading={historyLoading}
      isError={historyIsError}
      data={true}
    >
      <>
        {!userData?.userInfo.isFreeTrialActive &&
          !hasPlan &&
          !!fibiUserType && <EnhancedBanner isOver={true} />}
        {allMessages && (
          <div className="flex flex-col h-full gap-4  w-full md:w-[95%] xl:w-[80%] mx-auto">
            <div className="flex-1 overflow-auto">
              <ChatInterface
                messages={allMessages}
                isLoading={historyLoading}
                isError={historyIsError}
                interimTranscript={interimTranscript}
              />
            </div>
            <>
              {sessionCompleted &&
              sessionTemp &&
              (fibiUserType ? true : fibiClientToken) ? (
                <SessionEnded
                  messages={allMessages}
                  sessionTemp={sessionTemp}
                  fibiClientToken={fibiClientToken}
                  onInterviewOff={onInterviewOff}
                />
              ) : (
                <FiBiVoiceBottomBar
                  businessProps={{
                    currentSession: `${sessionTemp}/${userData?.maxSessionCount} Session`,
                    onEnd,
                    businessId: fibiClientToken,
                    activeSession: sessionTemp,
                  }}
                  onAddToChatHistory={onAddToChatHistory}
                  allMessages={allMessages}
                  audioRef={audioRef}
                  audioContextRef={audioContextRef}
                  onInterim={onInterim}
                  isDirectUser={!!fibiUserType}
                />
              )}
            </>

            <AiDisclaimer />
          </div>
        )}
      </>
    </RequestWrapper>
  );
};

export default BusinessMain;
